/**
 * The controller: owns state, talks to the KonkRide backend (REST + live socket), and paints the prototype's screens.
 * Screens are pure functions Snap -> HTML (views-*.ts). Events are delegated through data-act / data-in / data-file, as in the prototype.
 */
import { DOC_CERT, DRIVER_MAX_X, PRICE_FLOOR_NGN, fixProblem, haversineKm, sameCity, isLocalOnly, mapCityAt, type ClientMsg, type Fix, type LatLng, type NearbyDriver, type NearbyRider, type OfferView, type ServerMsg, type TripView } from '@konkride/shared';
import { API, ApiFail, api, tokenStore, upload } from '../lib/api';
import { Live, type ConnState } from '../lib/live';
import { prepareUpload } from '../lib/photo';
import { MT, MapView, type Line, type MapType, type Pin } from './map';
import { loadCityTiles } from './vtiles';
import { paintApp, setH } from './paint';
import { SHELL } from './shell';
import type { ChangeReq, DPrefs, DriverForm, DriverInfo, LedgerRow, LiveSnap, LoginForm, Peer, Prefs, RiderForm, RiderInfo, Snap, TripRow, UI, Veh } from './types';
import {
  LOCAL_VPICS, UNIT, VPICS, esc, future, gOverall, idOfLabel, isRegistered, labelOf, nameEq, parseSignupMd, phoneOk, pinShown, SAVE_MAIL_SUBJECT, signupFileName, plateOk, signupMd, driverMm, riderMm, yearOk } from './util';
import { accountScreen, sheetHtml } from './views-account';
import { chooseScreen, loginScreen, resetScreen } from './views-auth';
import { driverForm, driverHome, pricingScreen } from './views-driver';
import { docBlockMsg, driverGrade, driverIsLocal, isLocalDriver, noUnitsMsg, radOf, unitNeed, units } from './views-common';
import { engagement, needsAnswer } from './views-offer';
import { offerTop, riderHome, riderSignup } from './views-rider';
import { tripScreen } from './views-trip';

interface Me { user: { id: string; phone: string; name: string; units: number; role: 'rider' | 'driver' }; rider: RiderInfo | null; driver: DriverInfo | null; requests: ChangeReq[] }
interface City { id: string; name: string; lat: number; lng: number; radius_km: number }
interface Config { banks: string[]; cityAppKm: Record<string, number>; map: { tiles: string; attribution: string; vector?: { version: string; cities: string[] } } }

const blankD = (): DriverForm => ({ mode: 'city', name: '', phone: '', pin: '', plate: '', licExp: '', papExp: '', insExp: '', bank: '', vtype: '', make: '', brand: '', year: '', color: '', gr: {}, numOk: false, certOk: false, acct: '', chkErr: '', errors: [], perr: [], f: {}, busy: false });
const blankR = (): RiderForm => ({ name: '', phone: '', pin: '', bank: '', ok: false, errors: [], perr: [], busy: false, f: {} });
const blankL = (): LoginForm => ({ phone: '', pin: '', code: '', pin2: '', errors: [], busy: false });
const POSTED_KEEP_MS = 10 * 60_000; // a refresh within this long keeps the rider's posted request
const RP0: Prefs = { rg: 'city', lv: 'okada', mm: 'nyf', grade: 'any', asrc: 'app', offer: 1500, dest: null, destName: '', pickupName: '', rad: 3, pax: 0, online: true };
const DP0: DPrefs = { online: false, mm: 'both', rad: 3, dest: null, destName: '' };
const UP_KIND: Record<string, string> = { licFile: 'lic', phP: 'photoP', phF: 'photoF', papFile: 'rw', insFile: 'ins', vf: 'vf', vb: 'vb', vif: 'vif', vib: 'vib', idFile: 'riderId', rphP: 'riderPhoto' };
/** The driver's documents are every upload except the rider's two; a Local sign-up gives only its two photos and two vehicle pictures. */
const DOC_OF: Record<string, string> = Object.fromEntries(Object.entries(UP_KIND).filter(([f]) => f !== 'idFile' && f !== 'rphP'));
const LOCAL_FIELDS = ['phP', 'phF', 'vf', 'vb'];
const ACTIVE = (t: TripView | null): t is TripView => !!t && !['completed', 'cancelled'].includes(t.status);

const lsGet = <T,>(k: string, d: T): T => { try { const v = localStorage.getItem(k); return v ? { ...d, ...JSON.parse(v) } : d; } catch { return d; } };
/** Settings saved by older versions (internal names both and man) keep working. */
const riderPrefs = (): Prefs => { const p = lsGet('konkride.rp', RP0); return { ...p, mm: riderMm(p.mm) }; };
const driverPrefs = (): DPrefs => { const p = lsGet('konkride.dp', DP0); return { ...p, mm: driverMm(p.mm), online: false }; };
const lsSet = (k: string, v: unknown): void => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } };
const msgOf = (e: unknown): string => (e instanceof ApiFail ? e.message : e instanceof Error ? e.message : 'Something went wrong. Try again.');

export class KonkRideApp {
  private root!: HTMLElement;
  private cfg: Config = { banks: [], cityAppKm: {}, map: { tiles: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OpenStreetMap contributors' } };
  private cities: City[] = [];
  private cityId = (() => { try { return localStorage.getItem('konkride.city') || 'lagos'; } catch { return 'lagos'; } })();
  private me: Me | null = null;
  private ui: UI = { view: 'rider', screen: 'app', acct: '', sheet: '', dscr: '', tsort: 'new', lx: {}, see: null, later: false, bell: false, msg: '', paxFor: null, unitsFor: null, mt: 'road', mto: false, modal: null, paid: {}, posted: false, settings: false, rverr: [], backTo: null };
  private rp: Prefs = riderPrefs();
  private dp: DPrefs = driverPrefs();
  private dform = blankD(); private rform = blankR(); private lform = blankL();
  private files: Record<string, File> = {};
  private live = { conn: 'idle' as ConnState, units: 0, offers: [] as OfferView[], trip: null as TripView | null, auto: null as LiveSnap['auto'], autoFailed: false, drivers: [] as NearbyDriver[], riders: [] as NearbyRider[], counts: null as Record<string, number> | null, autos: 0, tripPos: null as LatLng | null };
  private pos: LatLng | null = null; private fix: Fix | null = null; private gpsReal = false; private gpsErr: string | null = 'Finding your location…'; private gpsFirst = true;
  private peer: Peer | null = null; private peerKey = '';
  private trips: TripRow[] = []; private ledger: LedgerRow[] = [];
  private names = new Map<string, string | false>(); private nameQ: Array<{ k: string; p: LatLng }> = []; private nameBusy = false; private nameT = 0;
  private imgs = new Map<string, string | null>(); private vehs = new Map<string, Veh | null>();
  private liveWs: Live | null = null; private liveKey = '';
  private mapv: MapView | null = null; private fly: LatLng | null = null;
  private raf = 0; private booted = false; private bootErr = '';
  private routes = new Map<string, { pts: Array<[number, number]>; km: number; min: number; real: boolean; t: number; kb: string }>(); private routeBusy = new Set<string>(); private routeAt = new Map<string, number>();
  private dsr: Array<{ name: string; lat: number; lng: number }> = []; private dsT = 0; private priceT = 0;
  private arrivedSent = new Set<string>();
  private ac: AudioContext | null = null; private beepT = 0; private wake: { release?: () => Promise<void> } | null = null;

  /* ================= start ================= */
  mount(root: HTMLElement): void {
    this.root = root;
    root.innerHTML = SHELL;
    const u = lsGet<{ view: UI['view']; tsort: string; mt: MapType; paid: Record<string, number>; posted: number; later: boolean }>('konkride.ui', { view: 'rider', tsort: 'new', mt: 'road', paid: {}, posted: 0, later: false });
    Object.assign(this.ui, { view: u.view, tsort: u.tsort, mt: u.mt, paid: u.paid, later: u.later });
    if (Date.now() - u.posted < POSTED_KEEP_MS) { this.ui.posted = true; this.postedAt = u.posted; } // a refresh keeps a request the rider just posted
    document.body.classList.add('flat');
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('click', this.onClickPick, true);
    document.addEventListener('input', this.onInput);
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('change', this.onChange);
    document.addEventListener('focusout', () => setTimeout(() => this.schedule(), 50));
    document.addEventListener('pointerdown', (e) => { this.gdrag = false; if ((e.target as HTMLElement).closest('.grab')) this.gy = e.clientY; });
    document.addEventListener('pointerup', this.onPointerUp);
    document.addEventListener('pointerdown', () => { this.pressedAt = Date.now(); }, true);
    for (const ev of ['pointerup', 'pointercancel']) document.addEventListener(ev, () => { this.pressedAt = 0; setTimeout(() => this.schedule(), 40); }, true);
    setInterval(() => this.tick(), 1000);
    this.gpsStart();
    this.schedule();
    void this.boot();
  }

  private postedAt = 0;
  private saveUi(): void { lsSet('konkride.ui', { view: this.ui.view, tsort: this.ui.tsort, mt: this.ui.mt, paid: this.ui.paid, posted: this.ui.posted ? this.postedAt : 0, later: this.ui.later }); }
  private async boot(): Promise<void> {
    try {
      const [cfg, cs] = await Promise.all([api<Config>('/config'), api<{ cities: City[] }>('/cities')]);
      this.cfg = cfg; this.cities = cs.cities;
      if (!this.cities.some((c) => c.id === this.cityId)) this.cityId = this.cities[0]?.id ?? 'lagos';
      if (tokenStore.get()) await this.refresh();
      await this.paystackReturn();
    } catch (e) { this.bootErr = msgOf(e); }
    this.booted = true;
    if (!this.me) this.ui.screen = 'choose';
    this.afterMe();
    this.schedule(true);
  }
  private async refresh(): Promise<void> {
    try { this.me = await api<Me>('/me'); } catch (e) { if (e instanceof ApiFail && e.status === 401) this.me = null; else throw e; }
    if (this.me) this.ui.view = this.me.user.role; // one account, one role: the app never offers the other side
  }
  /** After sign-in or any profile change: fill the forms from the saved profile and (re)connect. */
  private afterMe(): void {
    this.loadForms();
    this.syncLive();
  }
  private loadForms(): void {
    const m = this.me;
    if (!m) { this.dform = blankD(); this.rform = blankR(); return; }
    const d = m.driver, f: DriverForm['f'] = {};
    if (d) {
      const pics = (() => { try { return JSON.parse(d.pics_json || '{}') as Record<string, string>; } catch { return {}; } })();
      const have = (n: string, id: string | null | undefined) => { if (id) f[n] = { name: 'Uploaded', id }; };
      have('licFile', d.lic_key); have('papFile', d.rw_key); have('insFile', d.ins_key); have('phP', d.photo_p_key); have('phF', d.photo_f_key);
      for (const [k] of VPICS) have(k, pics[k]);
      this.dform = { ...blankD(), mode: isLocalOnly(d.vtype) ? 'local' : 'city', name: m.user.name, phone: m.user.phone, bank: d.bank || '', vtype: labelOf(d.vtype), plate: d.plate || '', make: d.make || '', brand: d.brand || '', year: d.year || '', color: d.color || '', gr: (() => { try { return JSON.parse(d.grade_json || '{}'); } catch { return {}; } })(), licExp: d.lic_exp || '', papExp: d.rw_exp || '', insExp: d.ins_exp || '', numOk: true, acct: d.bank ? m.user.name : '', f };
    } else this.dform = { ...blankD(), mode: this.dform.mode, name: m.user.name, phone: m.user.phone, vtype: this.dform.vtype };
    this.rform = { ...blankR(), name: m.user.name, phone: m.user.phone, bank: m.rider?.bank || '', f: m.rider ? { ...(m.rider.id_key ? { idFile: { name: 'Uploaded', id: m.rider.id_key } } : {}), ...(m.rider.photo_key ? { rphP: { name: 'Uploaded', id: m.rider.photo_key } } : {}) } : {} };
  }
  private async paystackReturn(): Promise<void> {
    const q = new URLSearchParams(location.search), ref = q.get('reference') || q.get('trxref');
    if (!ref) return;
    history.replaceState(null, '', location.pathname);
    if (!this.me) return;
    try {
      const r = await api<{ status: string; units: number }>(`/units/verify/${encodeURIComponent(ref)}`);
      this.live.units = r.units; this.me.user.units = r.units;
      this.ui.msg = r.status === 'paid' ? `Payment received. You now have ${r.units.toLocaleString('en-NG')} units.` : 'The payment was not completed. No money was taken.';
    } catch (e) { this.ui.msg = msgOf(e); }
  }

  /* ================= names, pictures, vehicles ================= */
  private city(): City | null { return this.cities.find((c) => c.id === this.cityId) ?? this.cities[0] ?? null; }
  private here = (p: LatLng | null | undefined): string => (p ? this.nameOf(p, 'Your current spot', 4) : '');
  private exact = (p: LatLng): string => this.nameOf(p, `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`, 3);
  private nameOf(p: LatLng, fb: string, dp: number): string {
    const k = p.lat.toFixed(dp) + ',' + p.lng.toFixed(dp), v = this.names.get(k);
    if (v) return v;
    if (v === undefined && !this.nameQ.some((q) => q.k === k)) { this.nameQ.push({ k, p: { lat: +p.lat.toFixed(dp), lng: +p.lng.toFixed(dp) } }); if (this.nameQ.length > 8) this.nameQ.shift(); this.pumpNames(); }
    return fb;
  }
  private pumpNames(): void {
    if (this.nameBusy || !this.nameQ.length || !this.me) return;
    this.nameBusy = true;
    const q = this.nameQ.shift()!, wait = Math.max(0, this.nameT + 1100 - Date.now());
    setTimeout(async () => {
      this.nameT = Date.now();
      try { const r = await api<{ name: string }>(`/geo/reverse?lat=${q.p.lat}&lng=${q.p.lng}`); this.names.set(q.k, r.name || false); this.schedule(); }
      catch { this.names.set(q.k, false); }
      this.nameBusy = false; this.pumpNames();
    }, wait);
  }
  private img = (id: string | null | undefined): string | undefined => {
    if (!id) return undefined;
    const c = this.imgs.get(id);
    if (c) return c;
    if (c === undefined) {
      this.imgs.set(id, null);
      const t = tokenStore.get();
      fetch(`${API}/uploads/${encodeURIComponent(id)}`, { headers: t ? { authorization: `Bearer ${t}` } : {} })
        .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('x'))))
        .then((b) => { this.imgs.set(id, URL.createObjectURL(b)); this.schedule(); })
        .catch(() => { /* leave the silhouette */ });
    }
    return undefined;
  };
  private buildVeh(x: { name?: string; plate?: string; vtype: string; make?: string | null; brand?: string | null; year?: string | null; color?: string | null; grade?: string | null; gr: Record<string, string>; pics: Record<string, string> }): Veh {
    const local = isLocalOnly(x.vtype);
    return {
      type: labelOf(x.vtype), make: x.make || '', brand: x.brand || '', year: x.year || '', color: x.color || '',
      // A Local vehicle has no grade and two pictures (front and back); its plate is shown like any other
      gr: local ? {} : x.gr, name: x.name ?? '', plate: x.plate ?? '', grade: local ? '' : x.grade ?? '', local,
      pics: (local ? LOCAL_VPICS : VPICS).map(([k, l]) => ({ l, id: x.pics[k], url: this.img(x.pics[k]) })),
    };
  }
  private veh = (driverId: string): Veh | undefined => {
    const m = this.me;
    if (m?.driver && driverId === m.user.id) {
      const d = m.driver;
      return this.buildVeh({ name: m.user.name, plate: d.plate, vtype: d.vtype, make: d.make, brand: d.brand, year: d.year, color: d.color, grade: d.grade, gr: (() => { try { return JSON.parse(d.grade_json || '{}'); } catch { return {}; } })(), pics: (() => { try { return JSON.parse(d.pics_json || '{}'); } catch { return {}; } })() });
    }
    const c = this.vehs.get(driverId);
    if (c) return this.buildVeh({ ...(c as unknown as Parameters<KonkRideApp['buildVeh']>[0]) });
    if (c === undefined) {
      this.vehs.set(driverId, null);
      api<{ name: string; plate: string; vtype: string; make: string; brand: string; year: string; color: string; grade: string; gr: Record<string, string>; pics: Record<string, string> }>(`/drivers/${encodeURIComponent(driverId)}/vehicle`)
        .then((v) => { this.vehs.set(driverId, v as unknown as Veh); this.schedule(true); })
        .catch(() => { this.vehs.delete(driverId); });
    }
    return undefined;
  };

  /* ================= GPS ================= */
  private gpsStart(): void {
    if (!('geolocation' in navigator)) { this.gpsErr = 'This browser cannot give a location.'; return; }
    try {
      navigator.geolocation.watchPosition(
        (p) => {
          this.takeFix(p);
          if (this.gpsFirst) { this.gpsFirst = false; this.fly = this.pos; this.followCity(); }
          this.onMoved();
        },
        (e) => { this.gpsReal = false; this.gpsErr = e.code === 1 ? 'Location is blocked. Allow location for this page to see where you are. Showing the city centre.' : 'Cannot get your location yet. Showing the city centre.'; this.schedule(); },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 }, // never an old position from the phone's memory
      );
    } catch { this.gpsErr = 'Location unavailable.'; }
  }
  private takeFix(p: GeolocationPosition): void {
    this.pos = { lat: p.coords.latitude, lng: p.coords.longitude }; this.gpsReal = true; this.gpsErr = '';
    this.fix = { ...this.pos, at: p.timestamp, acc: p.coords.accuracy };
  }
  /** Asks the phone for its position right now (not one from memory). Resolves either way; fixProblem then says whether the position can be used. */
  private refreshFix(): Promise<void> {
    return new Promise((done) => {
      if (!('geolocation' in navigator)) return done();
      navigator.geolocation.getCurrentPosition((p) => { this.takeFix(p); done(); }, () => done(), { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 });
    });
  }
  /** The city the real position is in becomes the chosen city (F14), unless a trip is on. True when it changed. */
  private followCity(): boolean {
    const f = this.fix; if (!f || ACTIVE(this.live.trip)) return false;
    const real = mapCityAt(f.lat, f.lng), mine = real && this.cities.find((c) => sameCity(c.id, real.id));
    if (!real || !mine || mine.id === this.cityId) return false;
    this.cityId = mine.id; try { localStorage.setItem('konkride.city', mine.id); } catch { /* ignore */ }
    this.syncLive(); return true;
  }
  /** '' when going online or posting a request may go ahead; otherwise the message for the person (F12, F14). The server checks again. */
  private async gpsGate(): Promise<string> {
    await this.refreshFix();
    const bad = fixProblem(this.fix, Date.now());
    if (bad) return bad;
    const real = mapCityAt(this.fix!.lat, this.fix!.lng);
    if (!real) return 'KonkRide is not in your area yet.';
    if (!sameCity(real.id, this.cityId)) return `You are in ${real.name}. Tap the location button to go to ${real.name}.`;
    return '';
  }
  private myPos(): LatLng | null { const c = this.city(); return this.pos ?? (c ? { lat: c.lat, lng: c.lng } : null); }
  private posSig = '';
  private onMoved(): void {
    const p = this.myPos(); if (!p) return;
    const sig = Math.round(p.lat * 2000) + ',' + Math.round(p.lng * 2000);
    if (sig !== this.posSig) { this.posSig = sig; this.presenceSoon(); }
    this.autoArrive();
    this.drawMap();
  }
  private autoArrive(): void {
    const t = this.live.trip, p = this.myPos();
    if (!ACTIVE(t) || t.status !== 'matched' || !p || !this.me || t.driverId !== this.me.user.id || !this.gpsReal) return;
    if (haversineKm(p, t.pickup) <= 0.25 && !this.arrivedSent.has(t.id)) { this.arrivedSent.add(t.id); this.send({ t: 'trip.action', id: t.id, action: 'arrived' }); }
  }

  /* ================= live connection ================= */
  private canLive(): boolean {
    const m = this.me, c = this.city();
    if (!m || !c || !this.myPos()) return false;
    return this.ui.view === 'rider' ? !!m.rider : m.driver?.status === 'approved';
  }
  private syncLive(): void {
    const key = this.canLive() ? `${this.ui.view}|${this.cityId}|${this.me!.user.id}` : '';
    if (key === this.liveKey) return;
    this.liveWs?.stop(); this.liveWs = null; this.liveKey = key;
    this.resetLive();
    if (!key) return;
    const l = new Live(this.ui.view, this.cityId, {
      onMsg: (m) => this.onMsg(m),
      onState: (s) => { this.live.conn = s; if (s === 'open') this.nearbySoon(); this.schedule(); },
      onKicked: () => { this.ui.msg = 'KonkRide is open on another device. Close it there to use it here.'; this.schedule(true); },
      getPresence: () => this.presence(),
      getPos: () => this.myPos(),
      onTrip: () => ACTIVE(this.live.trip),
    }, 30, 5);
    this.liveWs = l; l.start();
  }
  private resetLive(): void { this.live = { conn: 'idle', units: this.me?.user.units ?? 0, offers: [], trip: null, auto: null, autoFailed: false, drivers: [], riders: [], counts: null, autos: 0, tripPos: null }; this.peer = null; this.peerKey = ''; }
  private send = (m: ClientMsg): void => { this.liveWs?.send(m); };
  private presence(): ClientMsg & { t: 'presence' } {
    const at = this.myPos() ?? { lat: 0, lng: 0 }, m = this.me, shown = this.gpsReal; // the city centre is only a place to look at, never a position to be found at
    if (this.ui.view === 'driver') {
      const d = this.dp, local = !!m?.driver && isLocalDriver(m.driver);
      return { t: 'presence', role: 'driver', pos: at, online: d.online && shown, mm: local ? 'manual' : d.mm, rad: d.rad, dest: d.dest, destName: d.destName };
    }
    const x = this.rp, local = x.rg === 'local';
    return { t: 'presence', role: 'rider', pos: at, rg: x.rg, ...(local ? { lv: x.lv as never } : { grade: x.grade, asrc: x.asrc, ...(x.pax ? { pax: x.pax } : {}) }), mm: local ? 'manual' : x.mm, rad: x.rad, dest: x.dest, destName: x.destName, pickupName: x.pickupName, offer: x.offer, post: this.ui.posted && (x.mm === 'nyf' || local), online: x.online && shown };
  }
  private presenceSoon(): void { clearTimeout(this.pT); this.pT = window.setTimeout(() => { this.liveWs?.presence(); this.nearbySoon(); }, 350) as unknown as number; }
  private nearbySoon(): void { clearTimeout(this.nT); this.nT = window.setTimeout(() => this.askNearby(), 450) as unknown as number; }
  private nT = 0;
  private askNearby(): void { if (this.live.conn === 'open' && !ACTIVE(this.live.trip)) this.send({ t: 'nearby' }); }
  private pT = 0;
  private nearbyAt = 0;
  private onMsg(m: ServerMsg): void {
    const L = this.live;
    switch (m.t) {
      case 'state': L.units = m.units; L.offers = m.offers; L.trip = m.trip; L.auto = m.auto; this.tripChanged(); break;
      case 'nearby': if (m.drivers) L.drivers = m.drivers; if (m.riders) L.riders = m.riders; if (m.counts) L.counts = m.counts; if (m.autos !== undefined) L.autos = m.autos; break;
      case 'offer': L.offers = [...L.offers.filter((o) => o.id !== m.offer.id), m.offer]; break;
      case 'offer.end': L.offers = L.offers.filter((o) => o.id !== m.id); this.ui.msg = m.reason ?? (m.status === 'declined' ? 'Your request was declined.' : m.status === 'expired' ? 'Request timed out.' : this.ui.msg); break;
      case 'trip': {
        const ended = ['completed', 'cancelled'].includes(m.trip.status);
        L.trip = ended ? (L.trip?.id === m.trip.id ? m.trip : null) : m.trip;
        if (m.trip.status === 'matched') { L.offers = []; L.auto = null; L.tripPos = null; this.ui.posted = false; }
        this.tripChanged(); break;
      }
      case 'trip.pos': L.tripPos = m.pos; this.drawMap(); return;
      case 'auto': L.auto = m.auto; L.autoFailed = !!m.failed; break;
      case 'units': L.units = m.units; if (this.me) this.me.user.units = m.units; break;
      case 'error': this.ui.msg = m.message; if (m.code === 'docs_expired') this.dp = { ...this.dp, online: false }; break;
    }
    this.schedule();
  }
  private tripChanged(): void {
    const t = this.live.trip;
    if (!t) { this.peer = null; this.peerKey = ''; return; }
    const key = t.id + ':' + (t.status === 'completed' ? 'done' : 'on');
    if (key !== this.peerKey) {
      this.peerKey = key;
      api<Peer>(`/trips/${encodeURIComponent(t.id)}/peer`).then((p) => { if (this.peerKey === key) { this.peer = p; this.schedule(true); } }).catch(() => { /* keep names from the trip */ });
    }
    this.vehFor(t);
    this.autoArrive();
  }
  private vehFor(t: TripView): void { if (this.me && t.driverId !== this.me.user.id) this.veh(t.driverId); }

  /* ================= snapshot ================= */
  private snap(): Snap {
    const m = this.me, c = this.city();
    return {
      now: Date.now(), signedIn: !!m, user: m?.user ?? null, rider: m?.rider ?? null, driver: m?.driver ?? null, requests: m?.requests ?? [],
      ui: this.ui, live: this.live, rp: this.rp, dp: this.dp, pos: this.myPos(), gpsReal: this.gpsReal, gpsErr: this.gpsErr, appKm: (g) => this.cfg.cityAppKm[g] ?? null, roadKm: this.roadKm(),
      dform: this.dform, rform: this.rform, lform: this.lform, peer: this.peer, trips: this.trips, ledger: this.ledger,
      veh: this.veh, img: this.img, here: this.here, exact: this.exact, cityName: c?.name ?? '', banks: this.cfg.banks, codes: this.codes,
    };
  }
  private effectiveRp(): Prefs { return this.rp.rg === 'local' ? { ...this.rp, mm: 'manual' } : this.rp; }

  private body(s: Snap): string {
    if (this.bootErr && !this.booted) return `<div style="padding:20px;font:700 15px Nunito">${esc(this.bootErr)}</div>`;
    if (!this.booted) return '<p class="mute" style="padding:20px">Loading…</p>';
    const m = this.me, ui = this.ui;
    if (!m && ui.screen === 'choose') return chooseScreen();
    if (!m && ui.screen === 'login') return loginScreen(s, ui.view);
    if (!m && ui.screen === 'reset') return resetScreen(s);
    if (ui.acct) return accountScreen(s);
    let body = '', homeMode: 'lm' | 'cm' | '' = '';
    if (ui.view === 'driver') {
      if (!m?.driver) body = driverForm(s);
      else if (m.driver.status !== 'approved') body = '<span class="pill y">DRIVER</span><h1>Account on hold</h1><p class="mute">Your driver account is not active. Contact KonkRide support.</p>';
      else {
        homeMode = driverIsLocal(s) ? 'lm' : 'cm';
        const t = this.live.trip;
        body = t && t.driverId === m.user.id ? tripScreen(s, 'd') : ui.dscr === 'price' ? pricingScreen(s) : driverHome(s);
      }
    } else if (!m?.rider) body = riderSignup(s);
    else {
      const t = this.live.trip;
      homeMode = this.rp.rg === 'local' ? 'lm' : 'cm';
      body = t && t.riderId === m.user.id ? tripScreen(s, 'r') : riderHome({ ...s, rp: this.effectiveRp() });
    }
    if (homeMode) body = `<div class="${homeMode}"><div class="modebar">${homeMode === 'lm' ? '🛺 LOCAL MODE · inner city/town' : '🚗 CITY MODE · inter-city · auto or manual'}</div>${body}</div>`;
    return body;
  }
  private needMap(): boolean {
    const m = this.me, ui = this.ui;
    if (!m || ui.acct || !this.booted) return false;
    if (ui.view === 'rider') return !!m.rider;
    return m.driver?.status === 'approved' && (this.dp.online || !!this.live.trip);
  }

  /* ================= paint ================= */
  private schedule(force = false): void {
    if (force) this.force = true;
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => { this.raf = 0; const f = this.force; this.force = false; this.paint(f); });
  }
  private force = false;
  private painting = false;
  /** While a finger or mouse button is down the screen is not rebuilt, so a tap is never lost to a repaint between press and release. */
  private pressedAt = 0;
  private scrKey = '';
  private paint(force: boolean): void {
    if (this.painting) { this.schedule(force); return; }
    if (this.pressedAt && Date.now() - this.pressedAt < 1500) { this.force = this.force || force; return; }
    this.painting = true;
    try { this.paint0(force); } finally { this.painting = false; }
  }
  private paint0(force: boolean): void {
    const ae = document.activeElement as HTMLElement | null;
    const typing = !!ae && /^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName) && !!this.root.contains(ae) && ae.id !== 'dsq';
    const s = this.snap();
    this.drawBar(s);
    if (this.ui.posted !== !!this.postedAt) { this.postedAt = this.ui.posted ? Date.now() : 0; this.saveUi(); } // remember (or forget) a posted request across a page refresh
    setH(document.getElementById('engage'), engagement(s));
    setH(document.getElementById('sheet'), this.ui.modal ? `<div class="sheet"><div>${sheetHtml(s, this.ui.modal)}</div></div>` : '');
    if (typing && !force) return;
    const sc = document.getElementById('app')!;
    // A repaint keeps the scroll position, but a different screen (sign-up form to home, home to account, ...) starts at the top.
    const key = `${!!this.me}|${this.ui.screen}|${this.ui.view}|${!!this.ui.acct}|${!!(this.ui.view === 'driver' ? this.me?.driver : this.me?.rider)}`;
    const top = key === this.scrKey ? sc.scrollTop : 0;
    this.scrKey = key;
    const need = this.needMap(), min = this.ui.sheet === 'min', ml = document.getElementById('maplayer')!;
    ml.style.display = need ? 'block' : 'none';
    const cn = 'scroll' + (need ? ' withmap' : '');
    if (sc.className !== cn) sc.className = cn;
    paintApp(sc, need, min, this.body(s));
    if (sc.scrollTop !== top) sc.scrollTop = top;
    if (this.ui.backTo) { const bk = this.ui.backTo; this.ui.backTo = null; sc.querySelector(`[data-act=seemap][data-key="${bk}"]`)?.scrollIntoView({ block: 'center' }); }
    const sp = document.getElementById('showpill'); if (sp) sp.style.display = need && min ? 'block' : 'none';
    this.ensureMap(need);
    this.drawTools(need, s);
    this.drawSee();
    this.drawMap();
    this.fillDynamic();
    const ub = document.getElementById('urbtn'); if (ub) ub.style.display = '';
    const scity = document.getElementById('setcity'); if (scity) setH(scity, this.cities.length > 1 ? `<select id="citysel2" aria-label="City" ${ACTIVE(this.live.trip) ? 'disabled' : ''} style="height:40px;border:3px solid var(--ink);border-radius:12px;padding:0 10px;font:700 14px Nunito;width:100%">${this.cities.map((c) => `<option value="${esc(c.id)}" ${c.id === this.cityId ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}</select>` : '');
    const gs = document.getElementById('gstat'); if (gs) gs.textContent = 'Map: OpenStreetMap (free, no key).';
    document.getElementById('setp')?.classList.toggle('open', this.ui.settings && this.registered());
    this.syncSound();
  }
  private demoBar(s: Snap): string {
    const cs = this.cities.length > 1 ? `<select id="citysel" aria-label="City" ${ACTIVE(this.live.trip) ? 'disabled' : ''} style="height:34px;border:3px solid var(--ink);border-radius:999px;padding:0 10px;font:700 13px Nunito">${this.cities.map((c) => `<option value="${esc(c.id)}" ${c.id === this.cityId ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}</select>` : '';
    return `<small>${esc(s.cityName || 'KonkRide')}</small>${cs}<button data-act="gset">⚙ Settings</button>`;
  }
  private drawBar(s: Snap): void {
    const bar = document.getElementById('demo'), on = this.registered();
    if (bar) bar.style.display = on ? '' : 'none';
    setH(bar, on ? this.demoBar(s) : '');
  }
  /** Settings and the top bar belong to a registered account. Until then the app is only sign in and sign up. */
  private registered(): boolean { return isRegistered(this.me); }
  private drawTools(need: boolean, s: Snap): void {
    const t = document.getElementById('maptools'); if (!t) return;
    t.style.display = need ? 'flex' : 'none';
    const cur = (MT.find((x) => x[0] === this.ui.mt) ?? MT[0]!)[1];
    const tg = t.querySelector('[data-act=mtog]'); if (tg) tg.textContent = 'Map: ' + cur + ' ▾';
    const gl = t.querySelector('.gl'); if (gl) gl.textContent = this.gpsReal ? `${this.ui.view === 'driver' ? 'Drivers: Yellow circle, Riders: Green circle' : 'Drivers: Yellow circle'}. Radius view: ${radOf(this.ui.view === 'driver' ? this.dp.rad : this.rp.rad, this.ui.view === 'driver' ? this.dp.mm : this.rp.mm)} Km.` : (this.gpsErr ?? '');
    t.classList.toggle('open', this.ui.mto);
    const mm = t.querySelector('.mm'); if (mm) setH(mm as HTMLElement, MT.map((x) => `<button type="button" data-act="mtype" data-v="${x[0]}" class="${this.ui.mt === x[0] ? 'on' : ''}">${x[1]}<br><small style="${this.ui.mt === x[0] ? 'color:#ddd' : ''}">${x[2]}</small></button>`).join(''));
    void s;
  }
  private seeObj(): { x: { name: string; pos: LatLng; distKm: number }; } | null {
    const k = this.ui.see; if (!k) return null;
    if (this.live.trip || this.ui.acct || (this.ui.view === 'driver' ? k.k !== 'ri' : k.k !== 'dr')) { this.ui.see = null; return null; }
    const x = (k.k === 'dr' ? this.live.drivers : this.live.riders).find((z) => z.id === k.id);
    return x ? { x } : null;
  }
  private drawSee(): void {
    const b = document.getElementById('seebar'); if (!b) return;
    const o = this.seeObj();
    if (!o || this.ui.sheet !== 'min') { b.style.display = 'none'; return; }
    b.style.display = 'flex';
    setH(b, `<span>${esc(o.x.name)} · <span data-seekm>${o.x.distKm.toFixed(1)}</span> km from you</span><span style="flex-basis:100%;font-weight:700">📍 ${esc(this.exact(o.x.pos))}</span><button type="button" data-act="seeoff">Back to list</button>`);
  }

  /* ================= map ================= */
  private ensureMap(need: boolean): void {
    if (!need) return;
    const ml = document.getElementById('maplayer')!, p = this.myPos(); if (!p) return;
    if (!this.mapv) {
      this.mapv = new MapView(ml, { onTap: (q) => this.setDest(q), onDrag: (q) => this.setDest(q) }, p, this.cfg.map);
      this.mapv.setType(this.ui.mt);
      this.mapv.center(p, this.ui.sheet === 'min');
      const vec = this.cfg.map.vector, mc = vec ? mapCityAt(p.lat, p.lng) : null;
      if (vec && mc && vec.cities.includes(mc.id)) void loadCityTiles(mc.id, vec.version).then((pm) => { if (pm) this.mapv?.setVector(pm); });
    }
    this.mapv.invalidate();
    if (this.fly) { const f = this.fly; this.fly = null; this.mapv.center(f, this.ui.sheet === 'min'); }
  }
  private myDest(): LatLng | null { return this.ui.view === 'driver' ? this.dp.dest : this.rp.dest; }
  private routeSpecs(): Array<{ id: string; a: LatLng; b: LatLng; col: string }> {
    const t = this.live.trip, p = this.myPos(), m = this.me;
    if (ACTIVE(t) && m) {
      const driverPos = t.driverId === m.user.id ? p : this.live.tripPos ?? t.pickup;
      if (!driverPos) return [];
      if (t.status === 'matched') return [{ id: 'a', a: driverPos, b: t.pickup, col: '#FF4D8D' }, { id: 'b', a: t.pickup, b: t.dest, col: '#17A398' }];
      if (t.status === 'arrived') return [{ id: 'b', a: t.pickup, b: t.dest, col: '#17A398' }];
      return [{ id: 'a', a: driverPos, b: t.dest, col: '#FF4D8D' }];
    }
    const d = this.myDest();
    return d && p ? [{ id: 'me', a: p, b: d, col: '#FF4D8D' }] : [];
  }
  private routeFor(sp: { id: string; a: LatLng; b: LatLng }): { pts: Array<[number, number]>; km: number; min: number; real: boolean } | null {
    const k = [sp.a.lat, sp.a.lng, sp.b.lat, sp.b.lng].map((n) => n.toFixed(3)).join(','), kb = sp.b.lat.toFixed(4) + ',' + sp.b.lng.toFixed(4);
    const c = this.routes.get(k);
    if (c && (c.real || Date.now() - c.t < 30000)) { this.last.set(sp.id, { ...c, kb }); return c; }
    const L = this.last.get(sp.id);
    if (!this.routeBusy.has(k) && (!L || L.kb !== kb || Date.now() - (this.routeAt.get(sp.id) ?? 0) > 8000) && this.me) {
      this.routeAt.set(sp.id, Date.now()); this.routeBusy.add(k);
      api<{ pts: Array<[number, number]>; km: number; min: number }>(`/geo/route?alat=${sp.a.lat}&alng=${sp.a.lng}&blat=${sp.b.lat}&blng=${sp.b.lng}`)
        .then((r) => this.routes.set(k, { ...r, real: true, t: Date.now(), kb }))
        .catch(() => { const d = haversineKm(sp.a, sp.b); this.routes.set(k, { pts: [[sp.a.lat, sp.a.lng], [sp.b.lat, sp.b.lng]], km: d, min: 0, real: false, t: Date.now(), kb }); })
        .finally(() => { this.routeBusy.delete(k); this.drawMap(); this.fillDynamic(); });
    }
    return L && L.kb === kb ? L : null;
  }
  /** Road km to the chosen destination, only from a real route that was worked out for this destination (never a guess). */
  private roadKm(): number | null {
    const r = this.last.get('me'), d = this.myDest();
    return r?.real && d && r.kb === d.lat.toFixed(4) + ',' + d.lng.toFixed(4) ? r.km : null;
  }
  private last = new Map<string, { pts: Array<[number, number]>; km: number; min: number; real: boolean; kb: string }>();
  private drawMap(): void {
    if (!this.mapv || !this.needMap()) return;
    const p = this.myPos(); if (!p) return;
    const pins: Pin[] = [];
    this.live.drivers.forEach((d) => pins.push({ id: 'd' + d.id, pos: d.pos, col: '#FFC83D' }));
    if (this.ui.view === 'driver') this.live.riders.forEach((r) => pins.push({ id: 'r' + r.id, pos: r.pos, col: '#2EB85C' }));
    const t = this.live.trip, m = this.me;
    if (ACTIVE(t) && m) {
      if (t.driverId === m.user.id) pins.push({ id: 'rm', pos: t.pickup, col: '#2EB85C' });
      else pins.push({ id: 'dm', pos: this.live.tripPos ?? t.pickup, col: '#FFC83D' });
    }
    pins.push({ id: 'me', pos: p, col: '#2F7BFF', me: true });
    const so = this.seeObj(); if (so) pins.push({ id: 'see', pos: so.x.pos, col: '#FF4D8D' });
    const dest = ACTIVE(t) ? t.dest : this.myDest();
    if (dest) pins.push({ id: 'dest', pos: dest, col: '#FF4D8D', dia: true, drag: !ACTIVE(t) });
    const lines: Line[] = [];
    for (const sp of this.routeSpecs()) { const r = this.routeFor(sp); if (r) lines.push({ id: sp.id, pts: r.pts, col: sp.col, real: r.real }); }
    this.mapv.update(pins, lines);
  }
  private setDest(q: LatLng, name?: string, fly = false): void {
    if (ACTIVE(this.live.trip) || !this.me || this.ui.acct) return;
    const p: LatLng = { lat: +q.lat.toFixed(5), lng: +q.lng.toFixed(5) };
    if (this.ui.view === 'driver') {
      if (!this.me.driver || this.me.driver.status !== 'approved') return;
      this.dp = { ...this.dp, dest: p, destName: name ?? 'Pinned spot' }; lsSet('konkride.dp', this.dp);
    } else {
      if (!this.me.rider) return;
      this.rp = { ...this.rp, dest: p, destName: name ?? 'Pinned spot' }; lsSet('konkride.rp', this.rp);
    }
    if (fly) this.fly = p;
    this.last.delete('me'); this.presenceSoon(); this.schedule(true);
    if (!name) {
      api<{ name: string }>(`/geo/reverse?lat=${p.lat.toFixed(4)}&lng=${p.lng.toFixed(4)}`).then((r) => {
        if (!r.name) return;
        const cur = this.ui.view === 'driver' ? this.dp.dest : this.rp.dest;
        if (!cur || cur.lat !== p.lat || cur.lng !== p.lng) return;
        if (this.ui.view === 'driver') { this.dp = { ...this.dp, destName: r.name }; lsSet('konkride.dp', this.dp); } else { this.rp = { ...this.rp, destName: r.name }; lsSet('konkride.rp', this.rp); }
        this.presenceSoon(); this.schedule();
      }).catch(() => { /* keep "Pinned spot" */ });
    }
  }

  /* ================= 1-second tick ================= */
  private tick(): void {
    const now = Date.now();
    if (now - this.nearbyAt > 5000) { this.nearbyAt = now; this.askNearby(); }
    document.querySelectorAll<HTMLElement>('[data-exp]').forEach((e) => { e.textContent = String(Math.max(0, Math.ceil((+e.dataset.exp! - now) / 1000))); });
    this.fillDynamic();
    // expired offers disappear even if the server message is late
    const before = this.live.offers.length;
    this.live.offers = this.live.offers.filter((o) => o.expiresAt > now - 1500);
    if (before !== this.live.offers.length) this.schedule();
    if (this.needMap()) this.drawMap();
    if (this.dp.online && !this.wake && 'wakeLock' in navigator) (navigator as unknown as { wakeLock: { request: (t: string) => Promise<{ release: () => Promise<void> }> } }).wakeLock.request('screen').then((l) => { this.wake = l; }).catch(() => { /* optional */ });
    if (!this.dp.online && this.wake) { void this.wake.release?.(); this.wake = null; }
  }
  private fillDynamic(): void {
    document.querySelectorAll<HTMLElement>('[data-rt]').forEach((e) => {
      const r = this.last.get(e.dataset.rt!);
      e.textContent = r ? (e.dataset.pre ?? '') + r.km.toFixed(1) + (r.real ? ' km · ' + r.min + ' min' : ' km in a straight line (no road route)') : '';
    });
    document.querySelectorAll<HTMLElement>('[data-gpsnow]').forEach((e) => { const p = this.myPos(); if (p) { const t = this.here(p); if (e.textContent !== t) e.textContent = t; } });
    document.querySelectorAll<HTMLElement>('[data-dist]').forEach((e) => {
      const x = (e.dataset.dist === 'dr' ? this.live.drivers : this.live.riders).find((z) => z.id === e.dataset.id);
      if (x) e.textContent = 'Distance from you: ' + x.distKm.toFixed(1) + ' km';
    });
    const sk = document.querySelector<HTMLElement>('[data-seekm]'), so = this.seeObj(); if (sk && so) sk.textContent = so.x.distKm.toFixed(1);
  }
  /** Rider with a finished trip whose driver shows a bank account and who has not tapped Pay fare yet. */
  private payDue(): boolean {
    const t = this.live.trip, p = this.snap().peer;
    return this.ui.view === 'rider' && !!t && t.status === 'completed' && !!p?.bank && !this.ui.paid[t.id];
  }
  /** One beep: a request alert is one steady tone; the Pay fare alert is a rising three-note chime, so the two never sound alike. */
  private beep(kind: 'offer' | 'pay' | 'match'): void {
    try {
      const ac = this.ac!, now = ac.currentTime;
      const notes = kind === 'pay' ? [[523, 0], [659, 0.18], [988, 0.36]] : kind === 'match' ? [[660, 0], [880, 0.16]] : [[880, 0]];
      for (const [f, at] of notes) { const o = ac.createOscillator(), g = ac.createGain(); o.type = kind === 'offer' ? 'sine' : 'triangle'; o.frequency.value = f!; g.gain.value = 0.4; o.connect(g); g.connect(ac.destination); o.start(now + at!); o.stop(now + at! + (kind === 'pay' ? 0.16 : 0.25)); }
    } catch { /* sound is optional */ }
  }
  private beepKind: 'offer' | 'pay' | 'match' | '' = '';
  private matchAck = '';
  /** The other person made our match (accepted my request or took my price): sounds until I tap anything, so I know to continue. */
  private matchedByOther(me: string): boolean {
    const t = this.live.trip;
    return !!t && (t.status === 'matched' || t.status === 'arrived') && !!t.acceptedBy && t.acceptedBy !== me && this.matchAck !== t.id;
  }
  private syncSound(): void {
    const m = this.me;
    const offer = !!m && needsAnswer(this.snap());
    const kind: 'offer' | 'pay' | 'match' | '' = this.payDue() ? 'pay' : offer ? 'offer' : m && this.matchedByOther(m.user.id) ? 'match' : '';
    if (!kind) { clearInterval(this.beepT); this.beepT = 0; this.beepKind = ''; return; }
    if (this.beepT && this.beepKind === kind) return;
    clearInterval(this.beepT); this.beepKind = kind;
    if (!this.ac) { this.beepT = 0; return; }
    this.beepT = window.setInterval(() => this.beep(kind), kind === 'offer' ? 600 : 1400);
  }

  /* ================= input ================= */
  private gy: number | null = null; private gdrag = false;
  private onPointerUp = (e: PointerEvent): void => {
    if (this.gy === null) return;
    const dy = e.clientY - this.gy; this.gy = null;
    if (Math.abs(dy) > 30) {
      this.gdrag = true; setTimeout(() => { this.gdrag = false; }, 300);
      if (dy > 0 && this.ui.sheet !== 'min') { this.ui.sheet = 'min'; this.schedule(true); } else if (dy < 0 && this.ui.sheet === 'min') { this.ui.sheet = ''; this.schedule(true); }
    }
  };
  /** Enter in a sign-in or Forgot PIN field submits that screen, like the button. */
  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter' || e.isComposing || this.lform.busy) return;
    const id = (e.target as HTMLElement).id, scr = this.ui.screen;
    if (scr === 'login' && (id === 'lphone' || id === 'lpin')) { e.preventDefault(); void this.login(); }
    else if (scr === 'reset' && (id === 'lphone' || id === 'lcode' || id === 'lpin2')) { e.preventDefault(); void this.resetPin(); }
  };
  private onInput = (e: Event): void => {
    const t = e.target as HTMLInputElement | HTMLSelectElement;
    if (t.id === 'dsq') { this.searchPlaces((t as HTMLInputElement).value.trim()); return; }
    const k = t.dataset?.in; if (!k || k === 'tsort') return;
    let v = t.value;
    const d = this.dform, r = this.rform, l = this.lform, drv = this.me?.driver;
    if (k === 'km' || k === 'lkm' || k === 'offer') {
      const n = +v.replace(/\D/g, ''); t.value = n ? String(n) : '';
      if (k === 'offer') { this.rp = { ...this.rp, offer: n }; } else if (drv) { if (k === 'km') drv.city_km = n; else drv.local_km = n; }
      return;
    }
    const digits = ['phone', 'rphone', 'lphone', 'pin', 'rpin', 'lpin', 'lpin2'];
    if (digits.includes(k)) { v = v.replace(/\D/g, ''); t.value = v; }
    let redraw = false;
    switch (k) {
      case 'name': d.name = v; redraw = true; break;
      case 'phone': d.phone = v; d.acct = ''; d.chkErr = ''; d.numOk = false; redraw = true; break;
      case 'pin': d.pin = v; break;
      case 'plate': d.plate = v; break;
      case 'bank': d.bank = v; d.acct = ''; d.numOk = false; redraw = true; break;
      case 'vtype': d.vtype = v; redraw = true; break;
      case 'make': case 'color': (d as unknown as Record<string, string>)[k] = v; redraw = true; break;
      case 'brand': case 'year': (d as unknown as Record<string, string>)[k] = v; break;
      case 'licExp': case 'papExp': case 'insExp': (d as unknown as Record<string, string>)[k] = v; break;
      case 'rname': redraw = r.ok; r.ok = false; r.name = v; break;
      case 'rphone': r.phone = v; r.ok = false; redraw = true; break;
      case 'rpin': r.pin = v; break;
      case 'rbank': r.bank = v; r.ok = false; redraw = true; break;
      case 'pickup': this.rp = { ...this.rp, pickupName: v }; lsSet('konkride.rp', this.rp); break;
      case 'lphone': l.phone = v; redraw = this.ui.screen === 'reset'; break;
      case 'lpin': l.pin = v; break;
      case 'lpin2': l.pin2 = v; break;
      case 'lcode': l.code = v; break;
      default: return;
    }
    if (redraw) {
      const id = t.id, pos = (t as HTMLInputElement).selectionStart;
      this.paint(true);
      const el = document.getElementById(id) as HTMLInputElement | null;
      if (el && t.tagName === 'INPUT') { el.focus(); try { el.setSelectionRange(pos, pos); } catch { /* not a text field */ } }
    }
  };
  private onChange = (e: Event): void => {
    const t = e.target as HTMLInputElement;
    const k = t.dataset?.in;
    if (k === 'tsort') { this.ui.tsort = t.value; this.saveUi(); this.schedule(true); return; }
    if (k === 'km' || k === 'lkm') { const drv = this.me?.driver; if (drv) { if (k === 'km') drv.city_km = this.clampKm(false, +t.value || 0); else drv.local_km = this.clampKm(true, +t.value || 0); this.savePrice(); } this.schedule(true); return; }
    if (k === 'offer') { this.rp = { ...this.rp, offer: this.clampOffer(+t.value || 0) }; lsSet('konkride.rp', this.rp); this.presenceSoon(); this.schedule(true); return; }
    if (t.id === 'citysel' || t.id === 'citysel2') { if (ACTIVE(this.live.trip)) { this.schedule(true); return; } this.cityId = t.value; try { localStorage.setItem('konkride.city', t.value); } catch { /* ignore */ } this.fly = this.city() ? { lat: this.city()!.lat, lng: this.city()!.lng } : null; this.syncLive(); this.schedule(true); return; }
    if (t.dataset?.rec !== undefined && t.files?.[0]) { void this.loadSaved(t.files[0]); return; }
    const f = t.dataset?.file;
    if (f && t.files?.[0]) { const file = t.files[0]; t.value = ''; void this.pickFile(f, file); return; }
    if (k === 'licExp' || k === 'papExp' || k === 'insExp') { (this.dform as unknown as Record<string, string>)[k] = t.value; this.schedule(true); return; }
  };
  /** A picture or document the person chose: shrunk to 500 KB on the phone, or a plain message when it cannot be. */
  private async pickFile(field: string, file: File): Promise<void> {
    try {
      const ready = await prepareUpload(file);
      this.files[field] = ready; (field === 'idFile' || field === 'rphP' ? this.rform.f : this.dform.f)[field] = { name: ready.name };
    } catch (e) { this.ui.msg = msgOf(e); }
    this.schedule(true);
  }
  private onClickPick = (e: MouseEvent): void => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-pick]'); if (!b) return;
    const q = this.dsr[+b.dataset.pick!]; if (!q) return;
    e.stopPropagation(); this.dsr = [];
    this.setDest({ lat: q.lat, lng: q.lng }, q.name, true);
  };
  private searchPlaces(q: string): void {
    clearTimeout(this.dsT);
    const show = (msg = '') => { const el = document.getElementById('dsr'); if (el) el.innerHTML = this.dsr.map((x, i) => `<button type="button" data-pick="${i}">${esc(x.name)}</button>`).join('') + (msg ? `<span class="mute">${esc(msg)}</span>` : ''); };
    if (q.length < 2) { this.dsr = []; show(); return; }
    this.dsT = window.setTimeout(async () => {
      const p = this.myPos(); if (!p) return;
      try {
        const r = await api<{ places: Array<{ name: string; lat: number; lng: number }> }>(`/geo/search?q=${encodeURIComponent(q)}&lat=${p.lat}&lng=${p.lng}`);
        if ((document.getElementById('dsq') as HTMLInputElement | null)?.value.trim() !== q) return;
        this.dsr = r.places; show(r.places.length ? '' : 'No place found. Try another spelling or tap the map.');
      } catch { this.dsr = []; show('Search needs internet. You can still tap the map.'); }
    }, 500);
  }

  /* ================= clicks ================= */
  private onClick = (e: MouseEvent): void => {
    try { this.ac = this.ac ?? new AudioContext(); } catch { /* optional */ }
    if (this.me && this.matchedByOther(this.me.user.id)) { this.matchAck = this.live.trip!.id; this.syncSound(); } // any tap says "I saw it"
    const el = e.target as HTMLElement;
    const b = el.closest<HTMLElement>('[data-act]'); if (!b) return;
    const a = b.dataset.act!;
    if (a === 'sheet' && this.gdrag) { this.gdrag = false; return; }
    this.ui.msg = ''; this.ui.paxFor = null; this.ui.unitsFor = null;
    void this.act(a, b).catch((err) => { this.ui.msg = msgOf(err); this.schedule(true); });
  };
  private async act(a: string, b: HTMLElement): Promise<void> {
    const k = b.dataset, m = this.me, ui = this.ui;
    const done = (force = true) => this.schedule(force);
    switch (a) {
      case 'set': return this.setPref(k.k!, k.v!);
      case 'sheet': ui.sheet = ui.sheet === 'min' ? '' : 'min'; return done(true);
      case 'gset': ui.settings = !ui.settings; return done(true);
      case 'rtoggle': return this.toggleRider();
      case 'savemail': if (m) { location.href = `mailto:?subject=${encodeURIComponent(SAVE_MAIL_SUBJECT)}&body=${encodeURIComponent(this.detailsMd())}`; } return;
      case 'savecopy': {
        try { await navigator.clipboard.writeText(this.detailsMd()); b.textContent = 'Copied ✓'; } catch { b.textContent = 'Copy not allowed here'; }
        return;
      }
      case 'savemd': {
        if (!m) return;
        const url = URL.createObjectURL(new Blob([this.detailsMd()], { type: 'text/markdown' }));
        const a = Object.assign(document.createElement('a'), { href: url, download: signupFileName(m.user.name) });
        a.click(); URL.revokeObjectURL(url); return;
      }
      case 'pinvis': {
        const i = document.getElementById(k.for!) as HTMLInputElement | null; if (!i) return;
        const show = i.type === 'password'; i.type = show ? 'text' : 'password';
        if (show) pinShown.add(k.for!); else pinShown.delete(k.for!);
        b.textContent = show ? 'Hide' : 'Show'; b.setAttribute('aria-label', show ? 'Hide PIN' : 'Show PIN'); return;
      }
      case 'signout': this.signOut(); return done(true);
      case 'tologin': ui.screen = 'login'; this.lform = { ...blankL(), phone: this.dform.phone || this.rform.phone }; return done(true);
      case 'pickin': ui.view = (k.v === 'driver' ? 'driver' : 'rider'); this.saveUi(); this.lform = blankL(); ui.screen = 'login'; return done(true);
      case 'pick': ui.view = (k.v === 'driver' ? 'driver' : 'rider'); this.saveUi(); ui.screen = 'app'; return done(true);
      case 'tochoose': if (this.me) this.signOut(); else ui.screen = 'choose'; return done(true);
      case 'tojoin': ui.screen = 'app'; return done(true);
      case 'toreset': ui.screen = 'reset'; this.lform.errors = []; return done(true);
      case 'login': return this.login();
      case 'reset': return this.resetPin();
      case 'acct': ui.settings = false; ui.acct = (k.v ?? '') as UI['acct']; if (ui.acct === 'trips') void this.loadTrips(); if (ui.acct === 'units') void this.loadLedger(); if (ui.acct === 'profile') { this.loadForms(); } return done(true);
      case 'psave': return this.saveProfile();
      case 'dgr': this.dform.gr[k.k!] = k.v!; return done(true);
      case 'dmode': { const d = this.dform; if (d.mode !== k.v) { d.mode = k.v === 'local' ? 'local' : 'city'; d.vtype = ''; d.errors = []; d.numOk = false; } return done(true); }
      case 'numok': this.dform.numOk = !this.dform.numOk; return done(true);
      case 'certok': this.dform.certOk = !this.dform.certOk; return done(true);
      case 'bell': ui.bell = !ui.bell; return done(true);
      case 'chk': return this.checkBank();
      case 'dsubmit': return this.submitDriver();
      case 'rsignup': return this.submitRider();
      case 'rverify': return this.verifyRider();
      case 'pay': ui.modal = { k: 'pay' }; return done(true);
      case 'paid': return this.checkout();
      case 'sheetx': ui.modal = null; this.codesPin = ''; return done(true);
      case 'codesretry': {
        const got = await this.loadCodes(this.codesPin);
        ui.modal = got === 'ok' ? { k: 'save' } : { k: 'codesfail', err: 'Still could not load them. Check your connection and try again.' };
        return done(true);
      }
      case 'toggle': return this.toggleOnline();
      case 'later': ui.later = true; this.saveUi(); return;
      case 'nounits': this.lacksUnits(); return;
      case 'dscr': ui.dscr = (k.v ?? '') as UI['dscr']; return done(true);
      case 'rclr': ui.posted = false; this.rp = { ...this.rp, dest: null, destName: '' }; lsSet('konkride.rp', this.rp); this.last.delete('me'); this.presenceSoon(); return done(true);
      case 'dclr': this.dp = { ...this.dp, dest: null, destName: '' }; lsSet('konkride.dp', this.dp); this.last.delete('me'); this.presenceSoon(); return done(true);
      case 'lkset': { const d = m?.driver; if (d) { ui.dscr = ''; void this.patchSettings({ localKm: Math.max(50, d.local_km || 200) }).catch((e) => { this.ui.msg = msgOf(e); this.schedule(true); }); } return done(true); }
      case 'km': case 'lkm': { const d = m?.driver; if (d) { if (a === 'km') d.city_km = this.clampKm(false, (d.city_km || 0) + +k.n!); else d.local_km = this.clampKm(true, (d.local_km || 200) + +k.n!); this.savePrice(); } return done(true); }
      case 'offer': this.rp = { ...this.rp, offer: this.clampOffer((+this.rp.offer || 0) + +k.n!) }; lsSet('konkride.rp', this.rp); this.presenceSoon(); return done(true);
      case 'offerset': this.rp = { ...this.rp, offer: +k.n! }; lsSet('konkride.rp', this.rp); this.presenceSoon(); return done(true);
      case 'gps': this.rp = { ...this.rp, pickupName: this.here(this.myPos()) }; lsSet('konkride.rp', this.rp); return done(true);
      case 'rad': { const who = k.who === 'd' ? 'd' : 'r', cur = radOf(who === 'd' ? this.dp.rad : this.rp.rad, who === 'd' ? this.dp.mm : this.rp.mm), n = Math.min(20, Math.max(1, cur + +k.n!)); if (who === 'd') this.dp = { ...this.dp, rad: n }; else this.rp = { ...this.rp, rad: n }; lsSet(who === 'd' ? 'konkride.dp' : 'konkride.rp', who === 'd' ? this.dp : this.rp); this.presenceSoon(); return done(true); }
      case 'radr': if (k.who === 'd') this.dp = { ...this.dp, rad: 3 }; else this.rp = { ...this.rp, rad: 3 }; lsSet(k.who === 'd' ? 'konkride.dp' : 'konkride.rp', k.who === 'd' ? this.dp : this.rp); this.presenceSoon(); return done(true);
      case 'locx': ui.lx[k.key!] = !ui.lx[k.key!]; return done(true);
      case 'seemap': { const [kk, id] = k.key!.split(':'); ui.see = { k: kk as 'dr' | 'ri', id: id! }; ui.sheet = 'min'; const x = (kk === 'dr' ? this.live.drivers : this.live.riders).find((z) => z.id === id); if (x) this.fly = x.pos; return done(true); }
      case 'seeoff': ui.backTo = ui.see ? ui.see.k + ':' + ui.see.id : null; ui.see = null; ui.sheet = ''; return done(true);
      case 'mtog': ui.mto = !ui.mto; return done(true);
      case 'mtype': ui.mt = k.v as MapType; ui.mto = false; this.mapv?.setType(ui.mt); this.saveUi(); return done(true);
      case 'loc': { await this.refreshFix(); this.followCity(); const p = this.myPos(); if (p) { this.fly = p; } return done(true); } // the location button also takes the person back to the city they are really in
      case 'vcar': ui.modal = { k: 'veh', id: k.id! }; this.veh(k.id!); return done(true);
      case 'send': { const bad = await this.gpsGate(); if (bad) { ui.msg = bad; return done(true); } return this.sendOffer(k.id!, k.s as 'app' | 'own' | 'rider'); }
      case 'take': return this.takeRider(k.id!);
      case 'oacc': { const o = this.live.offers.find((z) => z.id === k.id); if (m?.driver && o && o.to.id === m.user.id && o.from.role === 'rider' && this.lacksUnits()) { return done(true); } this.send({ t: 'offer.accept', id: k.id! }); return; }
      case 'odec': this.send({ t: 'offer.decline', id: k.id! }); this.live.offers = this.live.offers.filter((o) => o.id !== k.id); return done(true);
      case 'req': { const r = this.rp; const bad = r.dest && r.pax ? await this.gpsGate() : ''; if (!r.dest) ui.msg = 'Tap the map to set your destination.'; else if (!r.pax) ui.msg = 'Choose how many passengers first.'; else if (bad) ui.msg = bad; else { this.live.autoFailed = false; this.liveWs?.presence(); this.send({ t: 'auto.start' }); } return done(true); }
      case 'rq0': this.send({ t: 'auto.stop' }); this.live.auto = null; return done(true);
      case 'request': return this.postRequest();
      case 'rcancel': ui.posted = false; this.rform.errors = []; this.presenceSoon(); return done(true);
      case 'mgo': return this.tripAct('start');
      case 'marr': return this.tripAct('arrived');
      case 'finish': return this.tripAct('complete');
      case 'mcancel': return this.tripAct('cancel');
      case 'mclr': this.live.trip = null; this.peer = null; this.peerKey = ''; this.live.tripPos = null; this.rp = { ...this.rp, pax: 0 }; lsSet('konkride.rp', this.rp); ui.posted = false; this.presenceSoon(); return done(true);
      case 'payfare': { const t = this.live.trip; if (t) { ui.paid[t.id] = Date.now(); this.saveUi(); } return done(true); }
      case 'plchg': ui.modal = { k: 'pl', step: 1 }; return done(true);
      case 'pladm': ui.modal = { k: 'pl', step: 3 }; return done(true);
      case 'plnext': { const n = (val('plnew')).toUpperCase(); if (!plateOk(n)) return this.modalErr('Enter your vehicle number.', 1); if (n === (m?.driver?.plate ?? '').toUpperCase()) return this.modalErr('That is already your vehicle number.', 1); ui.modal = { k: 'pl', step: 2, n }; return done(true); }
      case 'plgo': return this.changePlate(k.n!, val('plconf').toUpperCase());
      case 'pladmsend': return this.sendChange('plate', val('plnew').toUpperCase(), val('plwhy'));
      case 'phadm': ui.modal = { k: 'ph', f: k.f as 'phP' | 'phF' }; return done(true);
      case 'phsend': return this.sendChange(k.f === 'phP' ? 'photoP' : 'photoF', '', val('phwhy'));
    }
  }
  /** Signing out leaves nothing of the person behind: the session, the live connection, every form, list, cache and saved choice. */
  private signOut(): void {
    void api('/auth/logout', { method: 'POST' }).catch(() => undefined); // the server ends this token too; the app is signed out either way (the call reads the token before it is cleared)
    tokenStore.clear(); this.me = null; this.codes = []; this.codesPin = ''; pinShown.clear();
    this.liveWs?.stop(); this.liveWs = null; this.liveKey = ''; this.resetLive();
    this.trips = []; this.ledger = []; this.files = {}; this.vehs.clear(); this.imgs.clear();
    this.rp = { ...RP0 }; this.dp = { ...DP0 }; lsSet('konkride.rp', this.rp); lsSet('konkride.dp', this.dp);
    this.lform = blankL(); this.loadForms();
    Object.assign(this.ui, { screen: 'choose', acct: '', sheet: '', dscr: '', msg: '', modal: null, settings: false, posted: false, see: null, paxFor: null, unitsFor: null, rverr: [] });
  }
  private modalErr(err: string, step: 1 | 2 | 3, n?: string): void { this.ui.modal = { k: 'pl', step, err, ...(n ? { n } : {}) }; this.schedule(true); }

  /* ================= preferences ================= */
  private async setPref(key: string, v: string): Promise<void> {
    const rp = { ...this.rp }, dp = { ...this.dp };
    switch (key) {
      case 'r.rg': rp.rg = v as Prefs['rg']; this.ui.posted = false; if (rp.rg === 'local') this.send({ t: 'auto.stop' }); break;
      case 'r.lv': rp.lv = v; break;
      case 'r.mm': rp.mm = v as Prefs['mm']; if (v !== 'nyf') this.ui.posted = false; if (v !== 'auto') { this.send({ t: 'auto.stop' }); this.live.auto = null; } break;
      case 'r.pax': rp.pax = +v; break;
      case 'r.grade': rp.grade = v as Prefs['grade']; break;
      case 'r.src': rp.asrc = v as Prefs['asrc']; break;
      case 'd.mm': dp.mm = v as DPrefs['mm']; break;
      case 'd.lt': await this.patchSettings({ localMode: v === '1' }); break;
      case 'd.pm': await this.patchSettings({ pm: v as 'app' | 'own' | 'both' }); break;
    }
    this.rp = rp; this.dp = dp; lsSet('konkride.rp', rp); lsSet('konkride.dp', { ...dp, online: false });
    this.presenceSoon(); this.schedule(true);
  }
  private async patchSettings(body: Record<string, unknown>): Promise<void> {
    const r = await api<{ settings: { local_mode: number; pm: 'app' | 'own' | 'both'; city_km: number; local_km: number; local_km_set: number; local_app_km: number; rad: number } }>('/driver/settings', { method: 'PATCH', json: body });
    if (this.me?.driver) Object.assign(this.me.driver, r.settings);
    this.presenceSoon();
  }
  /** A driver's price per km: at least ₦100 (City) or ₦50 (Local), at most DRIVER_MAX_X times the app price for their grade or vehicle kind, as the server also checks. */
  private clampKm(local: boolean, v: number): number {
    const s = this.snap(), d = this.me?.driver, app = local ? d?.local_app_km ?? null : s.appKm(driverGrade(s)), top = app === null ? Infinity : DRIVER_MAX_X * app;
    return Math.min(top, Math.max(local ? 50 : 100, v));
  }
  /** A rider's price: at least the floor, and at most the limit when it is known on the screen (the server always checks). */
  private clampOffer(v: number): number { return Math.min(offerTop(this.snap()) ?? Infinity, Math.max(PRICE_FLOOR_NGN, v)); }
  private savePrice(): void {
    const d = this.me?.driver; if (!d) return;
    clearTimeout(this.priceT);
    this.priceT = window.setTimeout(() => { void this.patchSettings({ cityKm: Math.max(100, d.city_km || 100), ...(d.local_km_set ? { localKm: Math.max(50, d.local_km || 200) } : {}) }).catch((e) => { this.ui.msg = msgOf(e); this.schedule(true); }); }, 600);
  }
  /** True, and tells the driver, when the units left do not cover one accept (25 in Local, 50 in City). */
  private lacksUnits(): boolean {
    const s = this.snap();
    if (units(s) >= unitNeed(s)) return false;
    this.ui.msg = noUnitsMsg(this.snap()); return true;
  }
  private async toggleOnline(): Promise<void> {
    const blocked = docBlockMsg(this.snap());
    if (!this.dp.online && blocked) { this.ui.msg = blocked; this.schedule(true); return; } // the server refuses too
    if (!this.dp.online) { const bad = await this.gpsGate(); if (bad) { this.ui.msg = bad; this.schedule(true); return; } } // only a real, fresh position in this city goes online
    this.dp = { ...this.dp, online: !this.dp.online };
    if (!this.dp.online) this.cutRequests();
    this.liveWs?.presence(); this.schedule(true);
  }

  /** The rider's Online/Offline switch. Going offline cuts every request, the auto search and the sound at once. */
  private async toggleRider(): Promise<void> {
    if (!this.rp.online) { const bad = await this.gpsGate(); if (bad) { this.ui.msg = bad; this.schedule(true); return; } }
    this.rp = { ...this.rp, online: !this.rp.online }; lsSet('konkride.rp', this.rp);
    if (!this.rp.online) this.cutRequests();
    this.liveWs?.presence(); this.schedule(true);
  }
  /** Drops what the screen holds about requests, so nothing shows or sounds while the person is offline (the server cancels them too). */
  private cutRequests(): void {
    this.ui.posted = false; this.live.offers = []; this.live.auto = null; this.live.autoFailed = false;
    this.send({ t: 'auto.stop' });
  }

  /* ================= offers and trips ================= */
  private sendOffer(id: string, src: 'app' | 'own' | 'rider'): void {
    const m = this.me!;
    if (this.ui.view === 'driver') {
      if (this.lacksUnits()) { this.schedule(true); return; }
      this.send({ t: 'offer', to: id, src: 'rider', kind: 'manual' });
    } else {
      if (this.rp.rg !== 'local' && units(this.snap()) < UNIT) { this.ui.unitsFor = id; this.schedule(true); return; }
      if (!this.rp.pax && this.rp.rg !== 'local') { this.ui.paxFor = id; this.schedule(true); return; }
      this.liveWs?.presence();
      this.send({ t: 'offer', to: id, src, kind: 'manual' });
    }
    void m;
  }
  private takeRider(id: string): void {
    if (this.lacksUnits()) { this.schedule(true); return; }
    this.send({ t: 'take', rider: id });
  }
  private postRequest(): void {
    const r = this.rp, E: string[] = [], local = r.rg === 'local';
    if (!local && !r.pickupName.trim()) E.push('Enter a pickup point.');
    if (!r.dest) E.push('Enter a destination.');
    if (!local && !r.pax) E.push('Choose how many passengers.');
    if (!(r.offer > 0)) E.push('Set your price.');
    this.rform.errors = E;
    if (!E.length) { this.ui.posted = true; this.liveWs?.presence(); }
    this.schedule(true);
  }
  private tripAct(action: 'arrived' | 'start' | 'complete' | 'cancel'): void {
    const t = this.live.trip; if (!t) return;
    this.send({ t: 'trip.action', id: t.id, action });
  }

  /* ================= account: auth ================= */
  private async session(r: { token: string }, pin = ''): Promise<void> {
    tokenStore.set(r.token); this.codes = [];
    await this.refresh(); const got = await this.loadCodes(pin); // the codes are in before the home screen shows, so a quick tap on Save never saves an empty file
    this.ui.screen = 'app';
    this.afterMe();
    if (got === 'fail') this.ui.modal = { k: 'codesfail' };
  }
  private async login(): Promise<void> {
    const l = this.lform;
    if (!phoneOk(l.phone)) { l.errors = ['Enter your number as 234 followed by 10 digits, e.g. 2348036003122.']; return this.schedule(true); }
    if (!/^\d{6}$/.test(l.pin)) { l.errors = ['Your PIN is 6 digits.']; return this.schedule(true); }
    l.busy = true; l.errors = []; this.schedule(true);
    try { await this.session(await api('/auth/login', { json: { phone: l.phone, pin: l.pin, role: this.ui.view } }), l.pin); l.pin = ''; }
    catch (e) { l.errors = [msgOf(e)]; }
    l.busy = false; this.schedule(true);
  }
  /** The 12 recovery codes; they never change, so they are asked for once per sign-in. */
  private codes: string[] = [];
  /** 'fail' means the codes could not be loaded: the person is told and can try again. The PIN is kept only until the codes load or the notice is closed. */
  private codesPin = '';
  private async loadCodes(pin = ''): Promise<'ok' | 'fail' | 'skip'> {
    if (this.codes.length || !pin) return 'skip'; // the server asks for the PIN again; it is known right after sign-up and sign-in
    try { this.codes = (await api<{ codes: string[] }>('/me/recovery-codes', { json: { pin } })).codes; this.codesPin = ''; return 'ok'; }
    catch { this.codesPin = pin; return 'fail'; }
  }
  private detailsMd(): string {
    const u = this.me!.user;
    return signupMd(u.role, u.name, u.phone, new Date().toISOString().slice(0, 10), this.codes);
  }
  /** Forgot PIN: fills the number from a sign-up details file the person saved earlier. */
  private async loadSaved(file: File): Promise<void> {
    const l = this.lform, d = parseSignupMd(await file.text());
    if (!phoneOk(d.phone)) l.errors = ['This file has no readable number. Type it in instead.'];
    else if (d.role && d.role !== this.ui.view) l.errors = [`This file is for a ${d.role} account. Go back and choose ${d.role === 'driver' ? 'Driver' : 'Rider'}.`];
    else { l.phone = d.phone; l.errors = []; }
    this.schedule(true);
  }
  private async resetPin(): Promise<void> {
    const l = this.lform, E: string[] = [];
    if (!phoneOk(l.phone)) E.push('Enter your number as 234 followed by 10 digits, e.g. 2348036003122.');
    if (l.code.replace(/[^A-Za-z0-9]/g, '').length < 10) E.push('Enter one of your 12 recovery codes.');
    if (!/^\d{6}$/.test(l.pin2)) E.push('Your new PIN must be 6 digits.');
    l.errors = E;
    if (E.length) return this.schedule(true);
    l.busy = true; this.schedule(true);
    try { await this.session(await api('/auth/pin/reset', { json: { phone: l.phone, code: l.code.trim(), pin: l.pin2 } }), l.pin2); l.pin2 = ''; l.code = ''; }
    catch (e) { l.errors = [msgOf(e)]; }
    l.busy = false; this.schedule(true);
  }
  /** Creates the account the first time a new person has to talk to the server (bank name check or submit). */
  private async ensureAccount(name: string, phone: string, pin: string): Promise<void> {
    if (this.me) return;
    if (!/^\d{6}$/.test(pin)) throw new Error('Choose a 6-digit PIN first.');
    const r = await api<{ token: string }>('/auth/signup', { json: { phone, name: name.trim(), pin, role: this.ui.view } });
    tokenStore.set(r.token);
    this.me = await api<Me>('/me');
  }
  private async checkBank(): Promise<void> {
    const d = this.dform;
    if (!d.bank) return this.schedule(true);
    d.busy = true; d.errors = []; d.chkErr = ''; this.schedule(true);
    try {
      await this.ensureAccount(d.name, d.phone, d.pin);
      const r = await api<{ accountName: string }>('/bank/check', { json: { bank: d.bank, name: d.name.trim() } });
      d.acct = r.accountName;
    } catch (e) { d.chkErr = msgOf(e); }
    d.busy = false; this.schedule(true);
  }

  /* ================= account: driver ================= */
  private driverErrors(d: DriverForm, forProfile: boolean): string[] {
    const E: string[] = [], local = d.mode === 'local';
    if (d.name.trim().length < 3) E.push('Enter your full name.');
    if (!forProfile) {
      if (!phoneOk(d.phone)) E.push('Enter your number as 234 followed by 10 digits, e.g. 2348036003122.');
      else if (!local && !d.bank) E.push('Choose the bank for your account number.');
      else if (!local && !d.acct) E.push('Verify your account name first.');
      else if (!local && !nameEq(d.name, d.acct)) E.push('The bank account name must match the name in your app.');
    }
    if (!d.vtype) E.push('Choose your vehicle type.');
    if (!d.plate.trim()) E.push('Enter your vehicle number.');
    if (!d.f.phP) E.push('Upload your passport-size photo.');
    if (!d.f.phF) E.push('Upload your full-length photo.');
    if (local) {
      if (!(d.f.vf && d.f.vb)) E.push('Upload both vehicle pictures: front and back.');
    } else {
      if (!d.f.licFile) E.push('Upload your driver’s licence.');
      if (!future(d.licExp)) E.push('Licence expiry date must be in the future.');
      if (!d.f.papFile) E.push('Upload your roadworthiness certificate.');
      if (!future(d.papExp)) E.push('Roadworthiness expiry date must be in the future.');
      if (!d.f.insFile) E.push('Upload your insurance certificate.');
      if (!future(d.insExp)) E.push('Insurance expiry date must be in the future.');
      if (!d.make) E.push('Choose the manufacturer.');
      if (!(d.brand || '').trim()) E.push('Enter the brand, e.g. Corolla.');
      if (!yearOk(d.year)) E.push('Enter the vehicle year, e.g. 2015.');
      if (!d.color) E.push('Choose the vehicle colour.');
      if (!gOverall(d.gr)) E.push('Grade all four parts of your vehicle: body, interior, cooling and boot.');
      if (!(d.f.vf && d.f.vb && d.f.vif && d.f.vib)) E.push('Upload all four vehicle pictures: front, back, inside front, inside back.');
    }
    if (!local && (!forProfile || this.docsTouched(d)) && !d.certOk) E.push('Tick the box to certify your documents.');
    if (!forProfile && !d.numOk) E.push('Tick the box to confirm your phone number is correct.');
    return E;
  }
  /** New files or changed dates for the three documents: then the certification tick is needed again. */
  private docsTouched(d: DriverForm): boolean {
    const s = this.me?.driver;
    return !s || ['licFile', 'papFile', 'insFile'].some((k) => !!this.files[k]) || d.licExp !== (s.lic_exp ?? '') || d.papExp !== (s.rw_exp ?? '') || d.insExp !== (s.ins_exp ?? '');
  }
  /** Upload every file the person picked since the last save; returns the id of each document. */
  private async uploadAll(f: Record<string, { name: string; id?: string } | undefined>, only: string[]): Promise<void> {
    for (const field of only) {
      const file = this.files[field];
      if (file) { const id = await upload(UP_KIND[field]!, file); f[field] = { name: file.name, id }; delete this.files[field]; }
    }
  }
  private async putDriver(d: DriverForm, plate?: string): Promise<void> {
    const vtype = idOfLabel(d.vtype);
    if (!vtype) throw new Error('Choose your vehicle type.');
    const local = d.mode === 'local';
    await this.uploadAll(d.f, Object.keys(DOC_OF).filter((fld) => !local || LOCAL_FIELDS.includes(fld)));
    const docs: Record<string, string> = {};
    for (const [fld, key] of Object.entries(DOC_OF)) if (!local || LOCAL_FIELDS.includes(fld)) docs[key] = d.f[fld]?.id ?? '';
    const json = local
      ? { name: d.name.trim(), plate: plate ?? d.plate, vtype, docs, phoneConfirmed: true }
      : {
        name: d.name.trim(), plate: plate ?? d.plate, vtype, make: d.make, brand: d.brand.trim(), year: d.year, color: d.color,
        grade: { body: d.gr.body, int: d.gr.int, cool: d.gr.cool, boot: d.gr.boot }, docs, exp: { lic: d.licExp, rw: d.papExp, ins: d.insExp }, bank: d.bank, phoneConfirmed: true, ...(d.certOk ? { certify: DOC_CERT.version } : {}),
      };
    await api('/driver/profile', { method: 'PUT', json });
  }
  private async submitDriver(): Promise<void> {
    const d = this.dform;
    d.errors = this.driverErrors(d, false);
    if (!this.me && !/^\d{6}$/.test(d.pin)) d.errors.push('Choose a 6-digit PIN.');
    if (d.errors.length) return this.schedule(true);
    d.busy = true; this.schedule(true);
    try {
      await this.ensureAccount(d.name, d.phone, d.pin);
      await this.putDriver(d);
      await this.refresh();
      const got = await this.loadCodes(d.pin); d.pin = ''; this.afterMe(); if (got === 'fail') this.ui.modal = { k: 'codesfail' }; else if (this.codes.length) this.ui.modal = { k: 'save' };
    } catch (e) { d.errors = [msgOf(e)]; }
    d.busy = false; this.schedule(true);
  }
  private async saveProfile(): Promise<void> {
    const ui = this.ui, dv = ui.view === 'driver';
    if (dv && this.me?.driver) {
      const d = this.dform;
      d.perr = this.driverErrors(d, true);
      if (d.perr.length) return this.schedule(true);
      d.busy = true; this.schedule(true);
      try { await this.putDriver(d); await this.refresh(); this.loadForms(); } catch (e) { d.perr = [msgOf(e)]; d.busy = false; return this.schedule(true); }
      d.busy = false; ui.acct = ''; return this.schedule(true);
    }
    const r = this.rform, E: string[] = [];
    if (r.name.trim().length < 3) E.push('Enter your full name.');
    if (!r.f.idFile) E.push('Upload a photo of your ID.');
    if (!r.f.rphP) E.push('Upload your passport-size photo.');
    r.perr = E;
    if (E.length) return this.schedule(true);
    r.busy = true; this.schedule(true);
    try { await this.putRider(r); await this.refresh(); this.loadForms(); ui.acct = ''; } catch (e) { r.perr = [msgOf(e)]; }
    r.busy = false; this.schedule(true);
  }
  private async putRider(r: RiderForm): Promise<void> {
    await this.uploadAll(r.f, ['idFile', 'rphP']);
    await api('/rider/profile', { method: 'PUT', json: { name: r.name.trim(), bank: r.bank || undefined, idKey: r.f.idFile?.id, photoKey: r.f.rphP?.id } });
  }
  /** Create the rider profile. If the number was verified on the form, the badge is applied as part of it. */
  private async submitRider(): Promise<void> {
    const r = this.rform, E: string[] = [];
    if (r.name.trim().length < 3) E.push('Enter your full name.');
    if (!phoneOk(r.phone)) E.push('Enter your number as 234 followed by 10 digits, e.g. 2348036003122.');
    if (!r.f.idFile) E.push('Upload a photo of your ID.');
    if (!r.f.rphP) E.push('Upload your passport-size photo.');
    if (!this.me && !/^\d{6}$/.test(r.pin)) E.push('Choose a 6-digit PIN.');
    r.errors = E;
    if (E.length) return this.schedule(true);
    r.busy = true; this.schedule(true);
    try {
      await this.ensureAccount(r.name, r.phone, r.pin);
      await this.putRider(r);
      if (r.ok) await api('/rider/verify', { json: { bank: r.bank } });
      await this.refresh(); const got = await this.loadCodes(r.pin); r.pin = ''; this.afterMe(); if (got === 'fail') this.ui.modal = { k: 'codesfail' }; else if (this.codes.length) this.ui.modal = { k: 'save' };
    } catch (e) { r.errors = [msgOf(e)]; }
    r.busy = false; this.schedule(true);
  }
  /** The Verify button. At sign-up it only checks the bank name and marks the form verified; in Edit profile it applies the badge. */
  private async verifyRider(): Promise<void> {
    const r = this.rform, up = !this.registered(), bank = r.bank || this.me?.rider?.bank || '';
    const E: string[] = [];
    if (up && r.name.trim().length < 3) E.push('Enter your full name first.');
    if (up && !phoneOk(r.phone)) E.push('Enter your number as 234 followed by 10 digits.');
    if (up && !this.me && !/^\d{6}$/.test(r.pin)) E.push('Choose your 6-digit PIN first.');
    if (!bank) E.push('Choose your bank first.');
    this.ui.rverr = E;
    if (E.length) return this.schedule(true);
    r.busy = true; this.schedule(true);
    try {
      if (up) {
        await this.ensureAccount(r.name, r.phone, r.pin);
        const c = await api<{ accountName: string }>('/bank/check', { json: { bank, name: r.name.trim() } });
        if (!nameEq(r.name, c.accountName)) throw new Error('The bank account name must match the name above.');
        r.ok = true;
      } else { await api('/rider/verify', { json: { bank } }); await this.refresh(); }
    } catch (e) { this.ui.rverr = [msgOf(e)]; }
    r.busy = false; this.schedule(true);
  }
  private async changePlate(n: string, again: string): Promise<void> {
    if (again !== n) return this.modalErr('The numbers do not match. Type the new number again.', 2, n);
    try { await this.putDriver(this.dform, n); await this.refresh(); this.loadForms(); this.ui.modal = null; }
    catch (e) { this.modalErr(msgOf(e), 2, n); return; }
    this.schedule(true);
  }
  private async sendChange(field: 'plate' | 'photoP' | 'photoF', newValue: string, reason: string): Promise<void> {
    const plate = field === 'plate';
    const fail = (msg: string) => { this.ui.modal = plate ? { k: 'pl', step: 3, err: msg, n: newValue, why: reason } : { k: 'ph', f: field === 'photoP' ? 'phP' : 'phF', err: msg, why: reason }; this.schedule(true); };
    if (plate && !plateOk(newValue)) return fail('Enter your vehicle number.');
    if (reason.trim().length < 10) return fail('Write your reasons for changing it sooner.');
    try { await api('/driver/change-request', { json: { field, ...(plate ? { newValue } : {}), reason: reason.trim() } }); await this.refresh(); this.ui.modal = null; }
    catch (e) { return fail(msgOf(e)); }
    this.schedule(true);
  }
  private async loadTrips(): Promise<void> { try { this.trips = (await api<{ trips: TripRow[] }>(`/trips?role=${this.ui.view}`)).trips; } catch { this.trips = []; } this.schedule(true); }
  private async loadLedger(): Promise<void> { try { this.ledger = (await api<{ ledger: LedgerRow[] }>('/units')).ledger; } catch { this.ledger = []; } this.schedule(true); }
  private async checkout(): Promise<void> {
    try { const r = await api<{ url: string }>('/units/checkout', { json: { packs: 1 } }); location.assign(r.url); }
    catch (e) { this.ui.modal = { k: 'pay', err: msgOf(e) }; this.schedule(true); }
  }
}

const val = (id: string): string => ((document.getElementById(id) as HTMLInputElement | null)?.value ?? '').trim();
